<div class="flex flex-col gap-2">
  <!-- min teams message / create groups -->
  @if(tournament?.status === 'PENDING' && authService.hasRole(['admin', 'staff'])) {
    <div class="flex gap-2 items-center">
      <p-message icon="pi pi-info-circle" class="flex flex-1" [severity]="loading || teamsRaw().length >= environment.teamsMin ? 'info' : 'error'">
        {{ 'teams-min' | translate: {min: environment.teamsMin} }}
      </p-message>
      <p-button icon="pi pi-arrow-right" class="add-button mr-4" rounded (click)="createGroups($event)" 
        [pTooltip]="'button.generate-groups' | translate" tooltipEvent="hover" tooltipPosition="top"
        [disabled]="isEditing || teamsRaw().length < environment.teamsMin" 
        [ngClass]="{'blink': !isEditing && teamsRaw().length >= environment.teamsMin}" />
    </div>
  }
  <!-- teams table -->
  <p-table
    #teamsTable
    [value]="teams()"
    dataKey="id"
    [rows]="10"
    [paginator]="true"
    [paginatorDropdownAppendTo]="'body'"
    [rowsPerPageOptions]="[5, 10, 25, 50]"
    [tableStyle]="{ 'min-width': '600px' }"
    [loading]="loading"
    editMode="row"
    [editingRowKeys]="editingRowKeys"
    [expandedRowKeys]="expandedRows"
    stripedRows
  >
    <ng-template #header>
      <tr>
        <th style="width: 10px"></th>
        <th pSortableColumn="name">{{ 'field.name' | translate }}</th>
        <th pSortableColumn="center.name">{{ 'field.center' | translate }}</th>
        @if(!isEditing) {
          <th pSortableColumn="center.town">{{ 'field.town' | translate }}</th>
          <th pSortableColumn="center.province">{{ 'field.province' | translate }}</th>
          <th pSortableColumn="playersCount">{{ 'field.players' | translate }}</th>
        }
        <th>
          @if(tournament?.status === 'PENDING' && authService.hasRole(['admin', 'staff'])) {
            <div class="flex gap-2 justify-end">
              <p-button icon="pi pi-plus" class="add-button" rounded (click)="addTeam()" 
                [pTooltip]="(teamsRaw().length !== tournament?.maxTeams ? 'button.new-team' : 'teams-max-reached') | translate" 
                tooltipPosition="top" [disabled]="isEditing || teamsRaw().length === tournament?.maxTeams" />
            </div>
          }
        </th>
      </tr>
    </ng-template>
    <ng-template #body let-team let-expanded="expanded" let-editing="editing" let-ri="rowIndex">
      <tr [pEditableRow]="team">
        <!-- expand team players -->
        <td>
          <p-button type="button" pRipple [pRowToggler]="team" [text]="true" severity="secondary" 
            [rounded]="true" [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            [pTooltip]="!expanded ? ('button.show-players' | translate) : ''" tooltipEvent="hover" tooltipPosition="top"
            [disabled]="isEditing"/>
        </td>
        <!-- name -->
        <td>
          <p-cellEditor>
            <ng-template #input>
              <input pInputText type="text" [(ngModel)]="team.name" [style]="{ width: '100%' }"
                [placeholder]="'field.name' | translate" />
            </ng-template>
            <ng-template #output>
              {{ team.name }}
            </ng-template>
          </p-cellEditor>
        </td>
        <!-- center -->
        <td>
          <p-cellEditor>
            <ng-template #input>
              <p-select [options]="centers" [optionLabel]="'nameAndTown'" 
                appendTo="body" [(ngModel)]="team.center" [style]="{ width: '100%' }" 
                [placeholder]="'field.center' | translate"
                [filter]="true" filterBy="name"
              >
                <ng-template #item let-center>
                  <div [innerHTML]="center.nameAndTown"></div>
                </ng-template>
                <ng-template #selectedItem let-center>
                  @if(center) {
                    <div [innerHTML]="center.name"></div>
                  }
                </ng-template>
              </p-select>
            </ng-template>
            <ng-template #output>
              {{ team.center?.name }}
            </ng-template>
          </p-cellEditor>
        </td>
        @if(!isEditing) {
          <!-- town -->
          <td>
            {{ team.center?.town }}
          </td>
          <!-- province -->
          <td>
            {{ team.center?.province }}
          </td>
          <!-- playersCount -->
          <td class="text-center">
            {{ team.playersCount }}
          </td>
        }
        <!-- actions -->
        <td [ngStyle]="{'width': isEditing && editing ? '100px' : 'auto'}">
          <div class="flex items-center justify-end gap-2">
            @if(!isEditing && tournament?.status === 'PENDING' && authService.hasRole(['admin', 'staff'])) {
              <!-- add player -->
              <p-button icon="pi pi-user-plus" rounded severity="secondary"
                [pTooltip]="'button.add-player' | translate" tooltipPosition="left"
                (click)="toggleAddPlayerPop($event, team)"></p-button>
              <!-- edit team -->
              <p-button pInitEditableRow icon="pi pi-pencil" rounded severity="secondary" 
                [pTooltip]="'button.edit' | translate" tooltipPosition="top"
                (click)="onRowEditInit(team)"></p-button>
              <!-- delete team -->
              <p-button icon="pi pi-trash" rounded severity="danger"
                [pTooltip]="'button.delete' | translate" tooltipPosition="top"
                (click)="deleteTeam($event, team)"></p-button>
            }
            @if(isEditing && editing) {
              <!-- confirm edit -->
              <p-button pSaveEditableRow icon="pi pi-check" rounded severity="secondary"
                [pTooltip]="'button.save' | translate" tooltipPosition="top"
                [disabled]="!team.center" (click)="onRowEditSave(team)"></p-button>
              <!-- cancel edit -->
              <p-button pCancelEditableRow icon="pi pi-times" rounded severity="secondary"
                [pTooltip]="'button.cancel' | translate" tooltipPosition="top"
                (click)="onRowEditCancel(team, ri)"></p-button>
            }
          </div>
        </td>
      </tr>
    </ng-template>
    <!-- players table -->
    <ng-template #expandedrow let-team>
      <tr class="players">
        <td colspan="7">
          <div class="p-4">
            <h5>{{ 'field.team-players' | translate: {team: team.name} }}</h5>
            <p-table [value]="team.players" dataKey="id" stripedRows>
              <ng-template #header>
                <tr>
                  <th pSortableColumn="firstname">
                    {{ 'field.firstname' | translate }}
                  </th>
                  <th pSortableColumn="lastname">
                    {{ 'field.lastname' | translate }}
                  </th>
                  <th pSortableColumn="alias">
                    {{ 'field.alias' | translate }}
                  </th>
                  @if(tournament?.status === 'PENDING' && authService.hasRole(['admin', 'staff'])) {
                    <th></th>
                  }
                </tr>
              </ng-template>
              <ng-template #body let-player>
                <tr>
                  <td>{{ player.firstname }}</td>
                  <td>{{ player.lastname }}</td>
                  <td>{{ player.alias }}</td>
                  @if(tournament?.status === 'PENDING' && authService.hasRole(['admin', 'staff'])) {
                    <td>
                      <div class="flex items-center justify-center gap-2">
                        <p-button icon="pi pi-trash" rounded severity="danger"
                          [pTooltip]="'button.delete' | translate" tooltipPosition="top"
                          (click)="deletePlayer($event, player)"></p-button>
                      </div>
                    </td>
                  }
                </tr>
              </ng-template>
              <ng-template #emptymessage>
                <tr>
                  <td colspan="4">{{ 'no-item.players' | translate }}</td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template #emptymessage>
      <tr>
        <td colspan="7">{{ 'no-item.teams' | translate }}</td>
      </tr>
    </ng-template>
  </p-table>
</div>

<!-- confirm popup -->
<p-confirmpopup />

<!-- add player popup -->
<p-popover #addPlayerPop>
  <div class="p-confirmpopup-content">
    <p-autocomplete [placeholder]="'select-player' | translate" [(ngModel)]="newPlayer" [completeOnFocus]="true"
      [suggestions]="playersAvailableFiltered" (completeMethod)="filterPlayers($event)" optionLabel="name" />
  </div>
  <div class="p-confirmpopup-footer">
    <p-button [label]="'button.cancel' | translate" styleClass="p-confirmpopup-reject-button" size="small"
      (click)="addPlayerPop.hide()" severity="secondary"></p-button>
    <p-button [label]="'button.save' | translate" styleClass="p-confirmpopup-accept-button" size="small"
      (click)="addPlayer()"></p-button>
  </div>
</p-popover>

<!-- create groups confirm dialog -->
<p-confirmdialog #confirmGroups key="groups-create" [style]="{'max-width': '600px'}">
  <ng-template pTemplate="content" let-message>
    <div>{{ message.message }}</div>
  </ng-template>
  <ng-template pTemplate="footer">
    <p-button [label]="'button.cancel' | translate" styleClass="p-confirmpopup-reject-button" size="small"
      severity="secondary" (click)="confirmGroups.hide()"></p-button>
    <p-button [label]="'button.generate-groups' | translate" styleClass="p-confirmpopup-accept-button" size="small"
      (click)="confirmGroups.onAccept()"></p-button>
  </ng-template>
</p-confirmdialog>
